import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { SmartImage } from "@/components/site/SmartImage";
import { useT, useFormatters, useLocale } from "@/i18n/react";
import {
  fetchPublishedPrograms,
  fetchProgramTranslations,
  localizeProgram,
  programPhase,
  type ProgramRow,
  type ProgramTranslation,
} from "@/lib/db/programs";

export const Route = createFileRoute("/programs/")({
  staticData: { sitemap: true },
  loader: async ({ context }) => {
    const locale = (context as { locale?: string }).locale || "en";
    const [data, translations] = await Promise.all([
      fetchPublishedPrograms(),
      locale !== "en"
        ? fetchProgramTranslations(locale)
        : Promise.resolve({} as Record<string, ProgramTranslation>),
    ]);
    return { data, translations };
  },
  head: () => ({
    meta: [
      { title: "Programs | Koli & Coastal Communities - Bhoomiputra" },
      {
        name: "description",
        content:
          "Explore community programs, workshops and cohorts supporting Koli & Coastal Communities across Mumbai's coastline.",
      },
      {
        property: "og:title",
        content: "Programs | Koli & Coastal Communities - Bhoomiputra",
      },
      {
        property: "og:description",
        content:
          "Explore community programs, workshops and cohorts supporting Koli & Coastal Communities across Mumbai's coastline.",
      },
      { name: "twitter:title", content: "Programs | Koli & Coastal Communities - Bhoomiputra" },
      {
        name: "twitter:description",
        content:
          "Explore community programs, workshops and cohorts supporting Koli & Coastal Communities across Mumbai's coastline.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://bhoomiputra.org/og-image.jpg" },
      { name: "twitter:image", content: "https://bhoomiputra.org/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://bhoomiputra.org/programs" },
    ],
    links: [{ rel: "canonical", href: "https://bhoomiputra.org/programs" }],
  }),
  component: ProgramsPage,
});

type Filter = "all" | "upcoming" | "ongoing" | "past";

function ProgramsPage() {
  const loaderData = Route.useLoaderData();
  const t = useT();
  const [filter, setFilter] = useState<Filter>("all");
  const locale = useLocale();
  const { data = loaderData?.data, isLoading } = useQuery({
    queryKey: ["programs"],
    queryFn: fetchPublishedPrograms,
    initialData: loaderData?.data,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
  const { data: translations = loaderData?.translations } = useQuery({
    queryKey: ["program-translations", locale],
    queryFn: () => fetchProgramTranslations(locale),
    enabled: locale !== "en",
    initialData: loaderData?.translations,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const programs = (data ?? [])
    .filter((p) => filter === "all" || programPhase(p) === filter)
    .map((p) => localizeProgram(p, translations?.[p.id]));

  const current = programs.filter((p) => programPhase(p) !== "past");
  const past = programs.filter((p) => programPhase(p) === "past");

  return (
    <PageShell
      eyebrow={t("programs.eyebrow")}
      title={t("programs.title")}
      lede={t("programs.lede")}
    >
      <div className="mb-10 flex flex-wrap gap-2">
        {(["all", "upcoming", "ongoing", "past"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="rounded-full border px-4 py-2 text-sm transition"
            style={
              filter === f
                ? { background: "var(--deep-sea)", color: "white", borderColor: "var(--deep-sea)" }
                : { borderColor: "rgba(0,0,0,0.12)" }
            }
          >
            {t(`programs.filter.${f}` as "programs.filter.all")}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-ink/40">
          <Loader2 className="inline animate-spin" />
        </div>
      ) : programs.length === 0 ? (
        <p className="rounded-2xl bg-white p-10 text-center text-ink/60 ring-1 ring-black/5">
          {t("programs.empty")}
        </p>
      ) : (
        <div className="space-y-14">
          {current.length > 0 && (
            <section>
              {past.length > 0 && (
                <h2 className="mb-5 font-display text-2xl text-[color:var(--deep-sea)]">
                  {t("programs.sectionCurrent")}
                </h2>
              )}
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {current.map((p) => (
                  <ProgramCard key={p.id} program={p} />
                ))}
              </div>
            </section>
          )}

          {past.length > 0 && (
            <section>
              <h2 className="mb-5 font-display text-2xl text-ink/50">
                {t("programs.sectionPast")}
              </h2>
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {past.map((p) => (
                  <ProgramCard key={p.id} program={p} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </PageShell>
  );
}

function ProgramCard({ program }: { program: ProgramRow }) {
  const t = useT();
  const { date } = useFormatters();
  const phase = programPhase(program);
  const isPast = phase === "past";
  const phaseColor =
    phase === "ongoing"
      ? "var(--ocean)"
      : phase === "upcoming"
        ? "var(--deep-sea)"
        : "rgba(17,17,17,0.45)";

  return (
    <Link
      to="/programs/$slug"
      params={{ slug: program.slug }}
      className={`group flex h-full flex-col overflow-hidden rounded-2xl ring-1 transition-all duration-500 hover:-translate-y-1 ${
        isPast
          ? "bg-ink/[0.04] ring-black/5 opacity-80 hover:opacity-100 hover:shadow-[0_20px_40px_-25px_rgba(0,0,0,0.25)]"
          : "bg-white ring-black/5 hover:shadow-[0_30px_60px_-30px_rgba(0,60,90,0.35)]"
      }`}
    >
      <SmartImage
        src={program.cover_url ?? undefined}
        alt={`${program.title} - Bhoomiputra Foundation coastal community program`}
        fit="contain"
        className={`aspect-[4/3] ${isPast ? "grayscale transition duration-500 group-hover:grayscale-[35%]" : ""}`}
        width={1200}
        height={900}
      >
        <div className="absolute left-4 top-4">
          <span
            className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white"
            style={{ background: phaseColor }}
          >
            {t(`programs.status.${phase}` as "programs.status.ongoing")}
          </span>
        </div>
      </SmartImage>

      <div className="flex flex-1 flex-col p-5">
        <h2
          className={`font-display text-2xl leading-tight text-balance ${isPast ? "text-ink/60" : ""}`}
        >
          {program.title}
        </h2>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink/70">{program.summary}</p>
        <div className="mt-4 space-y-1.5 text-xs text-ink/60">
          {program.starts_at && (
            <div className="flex items-center gap-2">
              <CalendarDays size={13} />
              {date(program.starts_at, { day: "numeric", month: "short", year: "numeric" })}
            </div>
          )}
          <div className="flex items-center gap-2">
            <MapPin size={13} /> {program.venue || t("programs.venueTbc")}
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs">
          <span className="font-semibold text-ink/80">
            {program.is_paid && program.fee_paise > 0
              ? `₹${(program.fee_paise / 100).toLocaleString("en-IN")}`
              : t("programs.free")}
          </span>
          <span
            className={`inline-flex items-center gap-1 font-semibold ${
              isPast ? "text-ink/50" : "text-[color:var(--ocean)]"
            }`}
          >
            {t("programs.viewProgram")} <ArrowRight size={13} />
          </span>
        </div>
      </div>
    </Link>
  );
}
